/**
 * Deterministic engines — ported VERBATIM from krit_deal_engine_PROTOTYPE_v9.html (spec §9).
 *
 * The prototype read weights + IC threshold off a global STATE; here they are injected
 * as parameters (defaulting to the prototype defaults) so the module stays pure and
 * is reusable on both client and server. Behaviour is otherwise identical.
 */
import { MODULES, SECTORS, DEFAULT_WEIGHTS, DEFAULT_IC_THRESHOLD, type ModuleKey } from './constants.js';
import type {
  Deal,
  Grade,
  ProjectionRow,
  Reason,
  Recommendation,
  ThesisFit,
  Valuation,
  DiligenceStats,
} from './types.js';

/** parseFloat that never returns NaN — mirrors the prototype's num(). */
export function num(v: unknown): number {
  const n = parseFloat(v as string);
  return isNaN(n) ? 0 : n;
}

export function clamp(v: number, a: number, b: number): number {
  return Math.max(a, Math.min(b, v));
}

/**
 * The valuation P/E anchor for a sector. An unknown / unmapped sector degrades
 * gracefully to a market-median 25 rather than throwing — the sector list is
 * broad, but a deal may carry a bespoke or newly-added sector string.
 */
export function sectorPe(sector: string): number {
  return SECTORS[sector]?.pe ?? 25;
}

/** YoY revenue growth %, falling back to PAT growth, then 15%. */
export function growth(d: Pick<Deal, 'rev25' | 'rev26' | 'pat25' | 'pat26'>): number {
  return num(d.rev25) > 0
    ? (num(d.rev26) / num(d.rev25) - 1) * 100
    : num(d.pat25) > 0
      ? (num(d.pat26) / num(d.pat25) - 1) * 100
      : 15;
}

/** Krit Score = Σ(rating × weight) / Σ(weight), rounded to 0.1. */
export function score(d: Pick<Deal, 'ratings'>, weights: Record<ModuleKey, number> = DEFAULT_WEIGHTS): number {
  let t = 0;
  let w = 0;
  MODULES.forEach((m) => {
    const ww = num(weights[m.k] ?? m.w);
    t += clamp(num(d.ratings[m.k]), 0, 100) * ww;
    w += ww;
  });
  return w ? Math.round((t / w) * 10) / 10 : 0;
}

/** Grade band + display colour from a score. */
export function grade(s: number): Grade {
  if (s >= 90) return { g: 'A+', c: 'green' };
  if (s >= 80) return { g: 'A', c: 'green' };
  if (s >= 75) return { g: 'A−', c: 'teal' };
  if (s >= 70) return { g: 'B+', c: 'amber' };
  if (s >= 60) return { g: 'B', c: 'amber' };
  return { g: 'C', c: 'red' };
}

/** Parse the IPO-timeline label into years to listing. Default 2.5y. */
export function ipoYears(d: Pick<Deal, 'ipo'>): number {
  const ipo = d.ipo || '';
  const s = ipo.match(/(\d+)\s*[–-]\s*(\d+)\s*mo/);
  if (s) return (+s[1]! + +s[2]!) / 2 / 12;
  const y = ipo.match(/(\d+)\s*[–-]\s*(\d+)\s*yr/);
  if (y) return (+y[1]! + +y[2]!) / 2;
  const m = ipo.match(/(\d+)\s*mo/);
  if (m) return +m[1]! / 12;
  return 2.5;
}

/**
 * Valuation engine: implied P/E, margin of safety, and bear/base/bull MOIC scenarios.
 * Loss-making deals (pat26 ≤ 0) return ok=false with NaN for the meaningless fields.
 */
export function valn(d: Deal): Valuation {
  const patRaw = num(d.pat26);
  const pat = patRaw > 0 ? patRaw : 0.0001;
  const ask = num(d.valuationAsk) || 0.0001;
  const ok = patRaw > 0;
  const peNow = num(d.peNow) || sectorPe(d.sector);
  const peIPO = num(d.peIPO) || peNow * 0.8;
  const entryPE = ok ? ask / patRaw : NaN;
  const mos = ok && peNow > 0 ? (1 - ask / patRaw / peNow) * 100 : NaN;
  const g = clamp(growth(d), -20, 80);
  const yrs = ipoYears(d);
  const scen = (gm: number, pm: number, yr: number): number =>
    ok ? (pat * Math.pow(1 + gm / 100, yr) * pm) / ask : NaN;
  return {
    pat: patRaw,
    ask,
    peNow,
    peIPO,
    entryPE,
    mos,
    g,
    yrs,
    ok,
    base: scen(g, peIPO, yrs),
    bull: scen(g * 1.15, peIPO * 1.1, Math.max(1.5, yrs - 0.5)),
    bear: scen(g * 0.6, peIPO * 0.85, yrs + 1),
  };
}

/** Deterministic thesis-fit: Fit if 0 bad & ≥3 ok; Pass if ≥2 bad; else Borderline. */
export function thesisFit(d: Deal): ThesisFit {
  const v = valn(d);
  const r: Reason[] = [];
  const pat = num(d.pat26);
  const de = num(d.de);
  const mos = v.mos;
  const g = growth(d);

  if (pat > 0) r.push(['ok', 'Profitable · PAT ' + inr(pat)]);
  else r.push(['bad', 'Loss-making / pre-profit']);

  if (de < 0.5) r.push(['ok', 'Net D/E ' + de.toFixed(2) + 'x < 0.5x']);
  else if (de < 0.9) r.push(['warn', 'Leverage ' + de.toFixed(2) + 'x — above target']);
  else r.push(['bad', 'High leverage ' + de.toFixed(2) + 'x']);

  if (v.ok && mos >= 30) r.push(['ok', 'Entry ' + pct(mos) + ' below peers']);
  else if (v.ok && mos >= 15) r.push(['warn', 'MoS ' + pct(mos) + ' — thin vs 30%']);
  else r.push(['bad', 'Little / no discount to peers']);

  if (g >= 15) r.push(['ok', 'Revenue growth ' + pct(g)]);
  else if (g > 0) r.push(['warn', 'Growth ' + pct(g) + ' below 15%']);
  else r.push(['bad', 'Revenue not growing']);

  const bad = r.filter((a) => a[0] === 'bad').length;
  const ok = r.filter((a) => a[0] === 'ok').length;
  let verdict: ThesisFit['verdict'];
  let c: string;
  if (bad === 0 && ok >= 3) {
    verdict = 'Fit';
    c = 'green';
  } else if (bad >= 2) {
    verdict = 'Pass';
    c = 'red';
  } else {
    verdict = 'Borderline';
    c = 'amber';
  }
  return { verdict, c, reasons: r };
}

/** IC recommendation from score + valuation, gated on the (editable) IC threshold. */
export function rec(
  d: Deal,
  weights: Record<ModuleKey, number> = DEFAULT_WEIGHTS,
  icThreshold: number = DEFAULT_IC_THRESHOLD,
): Recommendation {
  const s = score(d, weights);
  const v = valn(d);
  if (s >= 80 && v.ok && v.mos >= 20)
    return {
      c: 'pass',
      i: '✓',
      h: 'PROCEED TO IC',
      s: 'Score ' + s + ' · MoS ' + pct(v.mos) + ' · ' + xx(v.base) + ' base MOIC',
    };
  if (s >= icThreshold)
    return { c: 'cond', i: '!', h: 'ADVANCE — conditions apply', s: 'Score ' + s + ' · close gaps before IC' };
  return { c: 'fail', i: '✕', h: 'HOLD / DO NOT ADVANCE', s: 'Score ' + s + ' below IC threshold ' + icThreshold };
}

/**
 * 5-year forward projection: revenue grown at held growth. EBITDA/PAT are projected ONLY when the
 * deck actually states them (a real FY26 margin to hold) — otherwise they stay 0 and the UI shows
 * "—". We never invent a margin: projecting profit the deck never gave is fabrication, not a forecast.
 */
export function proj(d: Deal): ProjectionRow[] {
  const g = clamp(growth(d), -10, 60) / 100;
  const rev0 = num(d.rev26);
  const eb0 = num(d.ebitda26);
  const pat0 = num(d.pat26);
  const ebM = rev0 && eb0 ? eb0 / rev0 : 0; // no stated EBITDA → don't project it
  const pm0 = rev0 && pat0 ? pat0 / rev0 : 0; // no stated PAT → don't project it
  const rows: ProjectionRow[] = [];
  for (let t = 1; t <= 5; t++) {
    const rev = rev0 * Math.pow(1 + g, t);
    const pm = pm0 ? clamp(pm0 + (0.02 * t) / 5, pm0, 0.3) : 0;
    rows.push({ y: 'FY' + (26 + t) + 'E', rev, eb: ebM ? rev * ebM : 0, pat: pm ? rev * pm : 0 });
  }
  return rows;
}

/** Diligence counts by status. */
export function diliStats(d: Pick<Deal, 'diligence'>): DiligenceStats {
  const x = d.diligence || [];
  return {
    total: x.length,
    flag: x.filter((i) => i.status === 'flag').length,
    open: x.filter((i) => i.status === 'open').length,
    cleared: x.filter((i) => i.status === 'cleared').length,
  };
}

/* ---- formatting helpers used inside engines (kept here for parity) ---- */

/** ₹ crore formatting, en-IN grouping. */
export function inr(v: number | null | undefined, d = 0): string {
  if (v == null || isNaN(v)) return '—';
  const n = Number(v);
  return (
    (n < 0 ? '-' : '') +
    '₹' +
    Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: d, minimumFractionDigits: d }) +
    ' Cr'
  );
}

export function pct(v: number | null | undefined, d = 0): string {
  if (v == null || isNaN(v)) return '—';
  return Number(v).toFixed(d) + '%';
}

export function xx(v: number | null | undefined, d = 1): string {
  if (v == null || isNaN(v)) return '—';
  return Number(v).toFixed(d) + 'x';
}

/** A period here ends an abbreviation, not a sentence ("… Deepak Nitrite Ltd." / "… Rs." / initials). */
const ABBREV = /\b(?:Ltd|Pvt|Inc|Corp|Cos?|Nos?|Mr|Mrs|Ms|Dr|Sr|Jr|St|vs|etc|approx|Rs|[A-Z])$/;

/**
 * Condense a blurb (e.g. a peer's screener "About") to one readable line: the first real
 * sentence if one ends within `max`, else a word-boundary clamp with an ellipsis. Sentence
 * breaks before char 40 or after an abbreviation are skipped so "Atul Ltd." doesn't end the line.
 */
export function oneLine(text: string | null | undefined, max = 150): string | null {
  const s = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  for (const m of s.slice(0, max + 1).matchAll(/[.!?](?=\s)/g)) {
    const end = m.index! + 1;
    if (end >= 40 && !ABBREV.test(s.slice(0, m.index!))) return s.slice(0, end);
  }
  if (s.length <= max) return s;
  // Clamp on a word boundary, then drop a dangling connective so it reads "…Products…", not "…Products, and…".
  const clamped = s
    .slice(0, max)
    .replace(/[\s,;:—-]+\S*$/, '')
    .replace(/\s+(?:and|or|the|of|in|at|on|its|with|for|to|an?|&)$/i, '')
    .replace(/[\s,;:&—-]+$/, '');
  return clamped + '…';
}
